import Vue from "vue";
import ElementUI from "element-ui";
import "element-ui/lib/theme-chalk/index.css";
import router from "./router";
import store from "./store";
import i18n from "@/locales";
import "@/styles/index.scss"; // global css
import "@/icons";
import App from "./App.vue";
import "vue-easytable/libs/theme-default/index.css"; // import style
import { VeTable, VePagination, VeLoading, VeLocale } from "vue-easytable";
import veTableEnUs from "vue-easytable/libs/locale/lang/en-US";

import Toast from "vue-toastification";
import "@/styles/modules/global-notification.scss";
import { handleRequestErrorPopup } from "./utils/request";
import { startVersionCheck } from "./utils/version-check";
import {
  reloadOnceForChunkError,
  installChunkReloadHandlers,
  isChunkLoadError,
} from "./utils/chunk-reload";

Vue.prototype.$handleRequestErrorPopup = handleRequestErrorPopup;

const options = {
  hideProgressBar: true,
  closeButton: false,
  closeOnClick: false,
  icon: {
    iconClass: "global-notification-icon",
    iconTag: "span",
  },
  position: "top-right",
};

Vue.use(Toast, options);

Vue.use(VeTable);
Vue.use(VePagination);
Vue.use(VeLoading);

Vue.prototype.$veLoading = VeLoading;
Vue.prototype.$veLocale = VeLocale;
// Without .use(), ve-table renders contextmenu/pagination labels as undefined.
VeLocale.use(veTableEnUs);

// Split imports into chunks
// Core UI framework - load first
Vue.use(ElementUI, {
  i18n: (key, value) => i18n.t(key, value),
  size: "mini",
  // Every popper (el-select dropdown, popover, cascader, date picker) is
  // body-appended and numbered by PopupManager from this base. At Element's
  // default of 2000 it lands *below* our own overlays — the nav drawer (2001),
  // the filter sheets (2050), the search sheet (2100), the AI studio (2400) —
  // so a dropdown opened inside any of them renders behind its own container
  // and reads as "the control does nothing". 3000 clears the tallest of them
  // and is the single place this is decided: overlays stay under 3000 and
  // never hand-set a popper's z-index. See $z-* in styles/variables.scss.
  zIndex: 3000,
});

// Load essential components
Vue.component("SimpleScrollbar", () =>
  import("./components/simple-scrollbar/simple-scrollbar.vue"),
);
Vue.component("Pagination", () => import("@/components/pagination"));
Vue.component("CommonPagination", () =>
  import("@/components/common-pagination"),
);
Vue.component("Loading", () => import("@/components/Loading"));

import { ComponentCell } from "./utils/componentCell";
Vue.component("ComponentCell", ComponentCell);

// Register essential filters
Vue.filter("tableFieldEmpty", function (value) {
  if (value === undefined || value === null) {
    return "-";
  }
  return typeof value === "number" ? value.toLocaleString() : value;
});

Vue.filter("convertFileSize", (bytes) => {
  if (bytes === 0) return "0 Byte";
  const units = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + units[i];
});

// Register directive with minimal processing
Vue.directive("removeAriaHidden", {
  bind(el) {
    const ariaEls = el.querySelectorAll(".el-radio__original");
    ariaEls.forEach((item) => item.removeAttribute("aria-hidden"));
  },
});

// Lazy load non-critical plugins
const setupNonCriticalPlugins = async () => {
  // Virtual scroller
  await import("vue-virtual-scroller/dist/vue-virtual-scroller.css");
  const VueVirtualScroller = await import("vue-virtual-scroller");
  Vue.use(VueVirtualScroller.default);

  // Simplebar
  await import("simplebar-vue/dist/simplebar.min.css");

  // Clipboard
  const Clipboard = await import("v-clipboard");
  Vue.use(Clipboard.default);

  await import("codemirror/lib/codemirror.css");
  const VueCodeMirror = await import("vue-codemirror");
  Vue.use(VueCodeMirror.default);

  const SortableObject = await import("sortablejs");
  const Sortable = SortableObject.default;
  const Swap = SortableObject.Swap;

  Sortable.mount(new Swap());
};

// Move sequence detection into a dedicated module
import sequenceState from "@/utils/sequenceState";
Object.keys(sequenceState).forEach((key) => {
  Object.defineProperty(Vue.prototype, `$${key}`, {
    get() {
      return sequenceState[key].value;
    },
    set(value) {
      sequenceState[key].value = value;
    },
  });
});

// Viewport awareness — `this.$viewport.{isMobile,isTablet,isDesktop,width}`.
// One rAF-throttled resize listener; booleans flip only at breakpoints.
import viewport, { initViewport } from "@/utils/viewport";
Object.defineProperty(Vue.prototype, "$viewport", {
  get() {
    return viewport;
  },
});
initViewport();

Vue.config.productionTip = false;

// `<lite-youtube>` is a native custom element registered by lite-youtube-embed
// (used in video-dialog.vue) — tell Vue not to treat it as an unknown component.
Vue.config.ignoredElements = [/^lite-youtube/];

// Chunk-404 recovery for errors surfaced via Vue's component path. Set before
// Sentry.init so Sentry chains onto (and preserves) it; non-chunk errors fall
// through to Sentry untouched.
Vue.config.errorHandler = (error) => {
  reloadOnceForChunkError(error);
};

import * as Sentry from "@sentry/vue";

const sentryIgnoreMessagePatterns = [
  /NavigationCancelled/i,
  /cancelled|canceled/i,
  // beforeRouteLeave guard calling next(false) (keep-editing) — expected, not a bug.
  /Navigation aborted from/i,
  // Our own AbortController.abort() when a request is superseded or its dialog
  // closes (SimilarCompany, AdDetailDialog, data-export-dialog) — NODE-EXPRESS-TF/T2.
  /signal is aborted without reason/i,
  /The operation was aborted/i,
  /px\.ads\.linkedin\.com/i,
  /linkedin/i,
  /Network Error/i,
  /Request failed with status code 401/i,
  /Redirected when going from/i,
  /window\.webkit\.messageHandlers/i,
  /Cannot set property className/i,
  /t\.className\.replace is not a function/i,
  // Firefox wording for the element-ui dropdown className write (NODE-EXPRESS-TS).
  /setting getter-only property "className"/i
];

// Browser-extension / injected-script filenames (our code is `js/` or `static/`).
const INJECTED_FRAME_FILENAME =
  /^(chrome|moz|safari-web|webkit)-extension:\/\/|(^|[\\/])injectedScript\.bundle\.js$/i;

// SDK wrapper frames ride on top of the real culprit — never the root cause.
const SENTRY_SDK_FILENAME = /(^|[\\/])@sentry[\\/+-]/i;

function isNonAppFrame(frame) {
  const filename = (frame?.filename ?? frame?.abs_path ?? "").trim();
  return (
    filename === "" ||
    filename === "<anonymous>" ||
    filename === "<unknown>" ||
    INJECTED_FRAME_FILENAME.test(filename) ||
    SENTRY_SDK_FILENAME.test(filename)
  );
}

// All-injected stack = extension error, not app-fixable (NODE-EXPRESS-SG/MZ/TH/SP).
// Real errors keep a real-source frame, so they still report.
function hasOnlyInjectedFrames(event) {
  const frames = (event?.exception?.values ?? []).flatMap(
    (value) => value?.stacktrace?.frames ?? [],
  );
  return frames.length > 0 && frames.every(isNonAppFrame);
}

function shouldDropSentryEvent(event) {
  const primaryException = event?.exception?.values?.[0];

  // Stale-deploy chunk 404s ("Loading (CSS) chunk X failed") are auto-recovered
  // by installChunkReloadHandlers (reload once), so they're expected deploy
  // churn, not real bugs. Drop them here — reusing isChunkLoadError as the
  // single source of truth — so recovered noise doesn't fill Sentry. A truly
  // broken deploy still surfaces via version-check's proactive update prompt.
  if (
    isChunkLoadError({
      name: primaryException?.type,
      message: primaryException?.value,
    })
  ) {
    return true;
  }

  const messageCandidates = [
    event?.message,
    // Whole chain, not just [0] — the real error is often a wrapped cause.
    ...(event?.exception?.values ?? []).flatMap((value) => [
      value?.value,
      value?.type,
    ]),
  ].filter(Boolean);

  const hasIgnoredMessage = messageCandidates.some((text) =>
    sentryIgnoreMessagePatterns.some((pattern) => pattern.test(String(text))),
  );

  if (hasIgnoredMessage) {
    return true;
  }
  return hasOnlyInjectedFrames(event);
}

Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  beforeSend(event) {
    if (shouldDropSentryEvent(event)) {
      return null;
    }
    return event;
  },
});

// Chunk-404 recovery for router navigation + global async paths.
installChunkReloadHandlers(router);

// Create app instance with minimal initial dependencies
new Vue({
  el: "#app",
  router,
  store,
  i18n,
  async mounted() {
    // Load non-critical plugins after app is mounted
    await setupNonCriticalPlugins();
    startVersionCheck();
  },
  render: (h) => h(App),
});
